import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../store/AuthContext'

interface TopbarProps {
  sidebarCollapsed?: boolean
}

export const Topbar = ({ sidebarCollapsed = false }: TopbarProps) => {
  const { user, theme, toggleTheme, logout } = useAuth()
  const navigate = useNavigate()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const handleLogoutClick = () => {
    setShowDropdown(false)
    setShowLogoutModal(true)
  }

  const confirmLogout = async () => {
    setShowLogoutModal(false)
    await logout()
    window.location.href = '/login'
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrador',
      manager: 'Gerente',
      viewer: 'Colaborador',
    }
    return labels[role] || role
  }

  return (
    <header className={`fixed top-0 right-0 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10 transition-all duration-300 ${
      sidebarCollapsed ? 'left-20' : 'left-64'
    }`}>
      <div className="flex items-center justify-end h-full px-6 gap-4">
        {/* Rules Button */}
        <button
          onClick={() => navigate('/regras')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Regras do Ownership"
        >
          <span className="text-xl">📖</span>
          <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">Regras</span>
        </button>

        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
        >
          {theme === 'light' ? (
            <>
              <span className="text-xl">🌙</span>
              <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">Dark</span>
            </>
          ) : (
            <>
              <span className="text-xl">☀️</span>
              <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">Light</span>
            </>
          )}
        </button>

        {/* User Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {/* Avatar */}
            {(user as any)?.avatar_url ? (
              <img
                src={(user as any).avatar_url}
                alt="Avatar"
                className="w-10 h-10 rounded-full object-cover border-2 border-primary/20"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                {user?.first_name?.[0] || user?.username?.[0] || 'U'}
              </div>
            )}

            {/* User Info */}
            <div className="text-left hidden md:block">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user?.first_name || user?.username}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {user?.role && getRoleLabel(user.role)}
              </p>
            </div>

            {/* Dropdown Arrow */}
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${
                showDropdown ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowDropdown(false)}
              />

              {/* Menu */}
              <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-20">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setShowDropdown(false)
                      navigate('/perfil')
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Meu Perfil
                  </button>
                  <button
                    onClick={handleLogoutClick}
                    className="w-full px-4 py-2 text-left text-sm text-danger hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Sair
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowLogoutModal(false)}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
              <div
                className="bg-white dark:bg-gray-800 rounded-card shadow-xl p-6 max-w-md w-full mx-4 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  Confirmar Saída
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Tem certeza que deseja sair do sistema?
                </p>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium rounded-btn transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmLogout}
                    className="px-6 py-2 bg-danger hover:bg-danger/90 text-white font-medium rounded-btn transition-colors"
                  >
                    Sair
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  )
}

export default Topbar
